'use client';

import { useEffect, useRef } from 'react';
import { registrarEvento } from '@/services/googleAnalytics';
import { Produto } from '@/types/produto';

interface UseSingleItemRowTrackerProps {
  items: Produto[];
  eventName: string;
  visibilityThreshold?: number;
  enabled?: boolean;
  minViewTime?: number; // Minimum time item must be visible (in ms)
}

export function useSingleItemRowTracker({
  items,
  eventName,
  visibilityThreshold = 0.5,
  enabled = true,
  minViewTime = 0,
}: UseSingleItemRowTrackerProps) {
  const viewedItems = useRef(new Set<number>());
  const observers = useRef<Map<number, IntersectionObserver>>(new Map());
  // eslint-disable-next-line no-undef
  const viewTimers = useRef<Map<number, NodeJS.Timeout>>(new Map());

  const registerElement = (itemIndex: number, element: HTMLElement | null) => {
    if (!element || !enabled) return;

    // Clean up existing observer
    const existingObserver = observers.current.get(itemIndex);
    if (existingObserver) {
      existingObserver.disconnect();
      observers.current.delete(itemIndex);
    }

    // Create observer for this item
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Item became visible
            if (minViewTime > 0) {
              // Set timer for minimum view time
              const timer = setTimeout(() => {
                if (!viewedItems.current.has(itemIndex)) {
                  viewedItems.current.add(itemIndex);
                  sendRowEvent(itemIndex);
                }
              }, minViewTime);

              viewTimers.current.set(itemIndex, timer);
            } else {
              // No minimum view time, trigger immediately
              if (!viewedItems.current.has(itemIndex)) {
                viewedItems.current.add(itemIndex);
                sendRowEvent(itemIndex);
              }
            }
          } else {
            // Item became invisible - cancel timer if exists
            const timer = viewTimers.current.get(itemIndex);
            if (timer) {
              clearTimeout(timer);
              viewTimers.current.delete(itemIndex);
            }
          }
        });
      },
      {
        threshold: visibilityThreshold,
        rootMargin: '0px',
      },
    );

    observer.observe(element);
    observers.current.set(itemIndex, observer);
  };

  const sendRowEvent = (itemIndex: number) => {
    const item = items[itemIndex];

    // Send analytics event
    registrarEvento(eventName, {
      produto: item.nome,
      linha: itemIndex + 1,
      total_de_itens: items.length,
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      observers.current.forEach((observer) => observer.disconnect());
      observers.current.clear();
      viewTimers.current.forEach((timer) => clearTimeout(timer));
      viewTimers.current.clear();
    };
  }, []);

  // Reset when items change
  useEffect(() => {
    viewedItems.current.clear();
    observers.current.forEach((observer) => observer.disconnect());
    observers.current.clear();
    viewTimers.current.forEach((timer) => clearTimeout(timer));
    viewTimers.current.clear();
  }, [items.length]);

  return {
    registerElement,
    viewedCount: viewedItems.current.size,
    totalCount: items.length,
    viewedPercentage:
      items.length > 0 ? (viewedItems.current.size / items.length) * 100 : 0,
  };
}
